import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { QuestionFormValues } from './types';
import { typesSupported } from './constants';
import {
    asyncPatchQuestion,
    asyncPostQuestion,
} from '../../../../../services/survey.service';
import { SwalCustomClass } from '../../../../components/general/toastSwal/swalCustomClass';
import { validations } from './validations';
import { initialValues } from './initialValues';

interface UseQuestionFormProps {
    idSurvey?: string;
    idQuestion?: string;
    question?: any;
    questionsDependency: any[];
    onClose: (value: boolean) => void;
    saveLoad: (value: boolean) => void;
}

export const useQuestionForm = ({
    idSurvey,
    idQuestion,
    question,
    questionsDependency,
    onClose,
    saveLoad,
}: UseQuestionFormProps) => {
    const [dependencies, setDependencies] = useState<any[]>([]);
    const [questionDependency, setQuestionDependency] = useState<any>();
    const [isTextTypeQuestion, setIsTextTypeQuestion] = useState(false);

    const getStartRange = () =>
        Number(questionDependency?.answers[0]?.range?.split('-')[0] || '1');

    const getEndRange = () =>
        Number(questionDependency?.answers[0]?.range?.split('-')[1] || '1');

    const showMSG = (message: string, isSuccess = true) =>
        SwalCustomClass.fire({
            text: message,
            icon: isSuccess ? 'success' : 'error',
            iconColor: '#1d1d1d',
        });

    const formik = useFormik<QuestionFormValues>({
        initialValues: initialValues(idSurvey),
        validationSchema: validations(
            questionDependency?.type,
            getStartRange,
            getEndRange,
            isTextTypeQuestion
        ),
        onSubmit: async (formModalQuestions) => {
            if (questionDependency?.type === 'linear') {
                formModalQuestions.answerDependency =
                    `${formModalQuestions.range1}-${formModalQuestions.range2}` ||
                    '';
            }

            if (!idQuestion) {
                if (
                    !['text', 'big-text', 'open-text', 'label'].includes(
                        formModalQuestions.type
                    )
                ) {
                    formModalQuestions.status = 'in process';
                } else {
                    if (formModalQuestions.type === 'label') {
                        formModalQuestions.required = false;
                    }
                    formModalQuestions.status = 'active';
                }
                const body: any = await asyncPostQuestion(
                    formModalQuestions
                ).finally(() => {
                    onClose(false);
                    saveLoad(true);
                    formik.handleReset;
                });
                if (body?.items !== undefined) {
                    showMSG('Pregunta creada exitosamente');
                } else if (body?.errors !== undefined) {
                    showMSG(body.message, false);
                }
            } else {
                asyncPatchQuestion(idQuestion, formModalQuestions)
                    .then((body: any) => {
                        if (body?.items !== undefined) {
                            showMSG('Pregunta actualizada exitosamente');
                        } else if (body?.errors !== undefined) {
                            showMSG(body.message, false);
                        }
                    })
                    .finally(() => {
                        onClose(false);
                        saveLoad(true);
                        formik.handleReset;
                    });
            }
        },
    });

    useEffect(() => {
        if (idQuestion && question) {
            formik.values.type = question?.type;
            formik.values.subtype = question?.subtype;
            formik.values.description = question?.description;
            formik.values.icon = question?.icon || 'circle';
            formik.values.required = question?.required;
            formik.values.gateSurveyUntilValidated =
                question?.gateSurveyUntilValidated ?? false;
            formik.values.why = question?.why;
            formik.values.dependency = question?.dependency;
            formik.values.status = question?.status;

            const dep = question?.questionDependency[0];

            formik.values.questionDependency = dep?.questionDependency?.id;
            setQuestionDependency(
                dependencies.filter(
                    (d) => d.id === dep?.questionDependency?.id
                )[0] || undefined
            );

            if (dep?.questionDependency.type !== 'linear') {
                formik.values.answerDependency = dep?.answerDependency?.id;
            } else {
                formik.values.range1 = dep?.answerTextDependency?.split('-')[0];
                formik.values.range2 = dep?.answerTextDependency?.split('-')[1];
            }
        }
    }, [idQuestion, question, dependencies]);

    useEffect(() => {
        if (
            idQuestion &&
            question &&
            formik.values.dependency &&
            formik.values.questionDependency !== questionDependency?.id
        ) {
            setQuestionDependency(
                dependencies.find(
                    (d) => d.id === formik.values.questionDependency
                )
            );

            formik.values.answerDependency = '';
            formik.values.range1 = '';
        }
    }, [formik.values]);

    useEffect(() => {
        const type = formik.values.type || question?.type;
        const isTextType = type === 'text' || type === 'big-text';

        setIsTextTypeQuestion(isTextType);
    }, [formik.values.type, question]);

    useEffect(() => {
        if (questionsDependency && question) {
            const data =
                questionsDependency.filter(
                    (q) =>
                        q.position < question.position &&
                        typesSupported[q?.type] &&
                        q.status !== 'inactive'
                ) || [];
            setDependencies(data);
        } else setDependencies([]);
    }, [questionsDependency, question]);

    return {
        formik,
        dependencies,
        questionDependency,
        setQuestionDependency,
        isTextTypeQuestion,
    };
};
